/**
 * Tâche 1148 (Eric 2026-06-16) — Bandeau « incident silencieux », en haut de
 * toutes les pages.
 *
 * Choix d'Eric : **aucun courriel** pour signaler ça — un courriel qui
 * annonce qu'aucun courriel ne part serait coupé par la même panne. Le
 * bandeau, lui, se voit forcément.
 *
 * Tâche 1158 (2026-06-17) — Une croix pour le masquer **4 heures**, et le
 * masquage tombe dès qu'un NOUVEL incident apparaît : on peut vouloir
 * travailler en paix sur un incident connu, jamais rater le suivant. Le
 * masquage est propre au navigateur de la personne, pas aux autres
 * administrateurs.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { Warning, X } from "@phosphor-icons/react";

import { useLang } from "../../contexts/LanguageContext";

const API = process.env.REACT_APP_BACKEND_URL;
const DISMISS_KEY = "knoria.silentIncidentBanner.dismissed";
const DISMISS_HOURS = 4;

// La signature change dès qu'un incident s'ajoute ou disparaît : c'est ce qui
// fait revenir le bandeau avant la fin des 4 heures.
const signatureOf = (state) =>
  (state?.incidents || [])
    .map((i) => i.id || i.reason || "")
    .sort()
    .join("|") || String(state?.count || 0);

const readDismissal = () => {
  try {
    return JSON.parse(window.localStorage.getItem(DISMISS_KEY) || "null");
  } catch {
    return null;
  }
};

export const SilentIncidentBanner = () => {
  // Tâche 1407 (Eric 2026-10-01) — Ce bandeau n'était pas bilingue du tout :
  // il s'affiche sur CHAQUE page d'admin, et restait entièrement en français
  // sur l'écran anglais. Le MOTIF, lui, est fabriqué au serveur et porte
  // désormais sa version anglaise (`reason_en`, motif 1324b) : aucun
  // dictionnaire du navigateur ne pouvait le traduire.
  const { lang, tr } = useLang();
  const [state, setState] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/api/system-notifications/silent-incidents`, {
        withCredentials: true,
      })
      .then((res) => {
        if (cancelled) return;
        setState(res.data);
        const saved = readDismissal();
        const stillFresh =
          saved &&
          saved.signature === signatureOf(res.data) &&
          Date.now() - Number(saved.at || 0) < DISMISS_HOURS * 3600 * 1000;
        setDismissed(Boolean(stillFresh));
      })
      // 403 pour un non-fondateur : le bandeau n'est simplement pas affiché.
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const dismiss = () => {
    try {
      window.localStorage.setItem(
        DISMISS_KEY,
        JSON.stringify({ at: Date.now(), signature: signatureOf(state) })
      );
    } catch {
      // Stockage refusé (navigation privée) : le masquage ne durera que
      // le temps de la page. Mieux que de ne rien masquer du tout.
    }
    setDismissed(true);
  };

  if (!state?.count || dismissed) return null;

  return (
    <div
      className="mb-3 flex items-start gap-3 rounded-xl border border-red-300 bg-red-50 px-4 py-2.5"
      data-testid="silent-incident-banner"
    >
      <Warning size={18} className="mt-0.5 shrink-0 text-red-600" />
      <a
        href="/dashboard/admin/email-send-log"
        className="text-sm text-red-900 flex-1 hover:underline"
        data-testid="silent-incident-banner-link"
      >
        <strong>
          {tr(
            `${state.count} incident(s) ouvert(s) depuis plus de ${state.silent_after_hours} h sans aucun courriel envoyé.`,
            `${state.count} incident(s) open for more than ${state.silent_after_hours} h with no email sent.`,
          )}
        </strong>{" "}
        {state.incidents?.[0]
          ? (lang === "fr"
              ? `${state.incidents[0].org_name} — ${state.incidents[0].reason}. `
              : `${state.incidents[0].org_name_en || state.incidents[0].org_name} — ${state.incidents[0].reason_en || state.incidents[0].reason}. `)
          : ""}
        {tr("Ouvrir l'audit des destinataires.", "Open the recipient audit.")}
      </a>
      <button
        type="button"
        onClick={dismiss}
        className="shrink-0 p-1 rounded text-red-700 hover:bg-red-100"
        title={tr(
          `Masquer ${DISMISS_HOURS} h (revient si un nouvel incident survient)`,
          `Hide for ${DISMISS_HOURS} h (comes back if a new incident occurs)`,
        )}
        aria-label={tr("Masquer ce bandeau", "Hide this banner")}
        data-testid="silent-incident-banner-dismiss"
      >
        <X size={16} />
      </button>
    </div>
  );
};

export default SilentIncidentBanner;
